import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';


const Billing: React.FC = () => {
  
  const {t:tSidebar} = useTranslation('sidebar_config');
  const location = useLocation();
  const navigate = useNavigate();

  const [selectedTab, setSelectedTab] = useState(0);
  const allBillingTabs = useMemo(
    () => [
      { key: 'package', label: tSidebar('package'), route: '/admin/billing/package' },
      { key: 'invoice', label: tSidebar('invoice'), route: '/admin/billing/invoice' },
      { key: 'advance', label: tSidebar('advance'), route: '/admin/billing/advance/advance-list' },
      { key: 'refund', label: tSidebar('refund'), route: '/admin/billing/refund/refund-list' },
      // { key: 'pharmacy', label: tSidebar('pharmacy'), route: '/admin/billing/pharmacy' },
    ],
    [tSidebar]
  );

  const visibleTabs = allBillingTabs;


  // Map routes to tab index
  const tabRoutes = useMemo(() => visibleTabs.map((t) => t.route), [visibleTabs]);

  const getTabBaseRoute = useCallback((route: string) => {
    if (route.startsWith('/admin/billing/refund')) return '/admin/billing/refund';
    if (route.startsWith('/admin/billing/advance')) return '/admin/billing/advance';
    if (route.startsWith('/admin/billing/invoice')) return '/admin/billing/invoice';
    if (route.startsWith('/admin/billing/package')) return '/admin/billing/package';
    if (route.startsWith('/admin/billing/pharmacy')) return '/admin/billing/pharmacy';
    return route;
  }, []);


  useEffect(() => {
    if (visibleTabs.length === 0) {
      setSelectedTab(0);
      return;
    }

    const tabIndex = visibleTabs.findIndex((tab) =>
      location.pathname.startsWith(getTabBaseRoute(tab.route))
    );
    setSelectedTab(tabIndex !== -1 ? tabIndex : 0);
  }, [location.pathname, tabRoutes, visibleTabs, navigate, getTabBaseRoute]);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number): void => {
    if (!tabRoutes[newValue]) return;
    setSelectedTab(newValue);
    navigate(tabRoutes[newValue]);
  };

  // Only show tabs for these billing subpages
  const showTabs = [
    '/admin/billing/package',
    '/admin/billing/invoice',
    '/admin/billing/advance/advance-list',
    '/admin/billing/refund/refund-list',
    // '/admin/billing/pharmacy'
  ].includes(location.pathname);

  return (
    <Box data-testid="billing-page-container">
      {showTabs && visibleTabs.length > 0 && (
        <Tabs
          data-testid="billing-tabs"
          value={selectedTab}
          onChange={handleTabChange}
          centered
          variant='fullWidth'
          sx={{ mb: 2 }}
          TabIndicatorProps={{ style: { backgroundColor: '#E17E61' } }}
        >
          {visibleTabs.map((label, index) => {
            return (
              <Tab
                key={label.key}
                data-testid={`billing-tab-${label.key}`}
                label={label.label}
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: selectedTab === index ? 600 : 500,
                  color: '#BBBBBB',
                  textTransform: 'none',
                  '&.Mui-selected': {
                    color: '#E17E61',
                    fontWeight: 600,
                  },
                }}
              />
            );
          })}
        </Tabs>
      )}
      <Box data-testid="billing-content" p={2} overflow='auto'>
        <Outlet />
      </Box>
    </Box>
  );
};

export default Billing;
